import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Sheet,
    Typography,
    Box,
    Button,
    Table,
    IconButton,
    Chip,
    Tabs,
    tabClasses,
    TabList,
    Tab,
    TabPanel,
    Breadcrumbs,
    Link,
    Divider,
    CircularProgress,
    Alert,
    Select,
    Option,
    FormControl
} from '@mui/joy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import domain from '../utils/domain';

interface Exam {
    id: number;
    title: string;
    description: string;
    duration: number;
    total_marks: number;
    passing_marks: number;
    negative_marking: number;
    status: 'draft' | 'active' | 'inactive' | 'completed';
    created_at: string;
}

interface Section {
    id: number;
    name: string;
    description: string;
    easy_questions: number;
    medium_questions: number;
    hard_questions: number;
    total_questions: number;
    marks_per_question: number;
    created_at: string;
}

interface Question {
    id: number;
    section_id: number;
    section_name: string;
    question_text: string;
    question_type: 'MCQ' | 'TRUE_FALSE';
    difficulty_level: 'easy' | 'medium' | 'hard';
    option_a: string;
    option_b: string;
    option_c?: string;
    option_d?: string;
    correct_answer: number;
    explanation?: string;
    image_url?: string;
    created_at: string;
}

interface ExamSection {
    id: number;
    exam_id: number;
    section_id: number;
    section_order: number;
    easy_questions: number;
    medium_questions: number;
    hard_questions: number;
    total_questions: number;
}

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') === 'sections' ? 1 : params.get('tab') === 'questions' ? 2 : 0;
    });
    const [exams, setExams] = useState<Exam[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [selectedSection, setSelectedSection] = useState<number | null>(null);

    useEffect(() => {
        if (activeTab === 0) {
            fetchExams();
        } else if (activeTab === 1) {
            fetchSections();
        } else if (activeTab === 2) {
            console.log('Questions tab selected, fetching sections and questions...');
            fetchSections();
            fetchQuestions();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 2 && !isLoading) {
            console.log('Selected section changed, fetching questions...');
            fetchQuestions();
        }
    }, [selectedSection]);

    const fetchExams = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch exams');
            }

            const data = await response.json();
            setExams(data.exams);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exams');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSections = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            console.log('Fetching sections...');
            const response = await fetch(`${domain}/api/admin/sections`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch sections');
            }

            const data = await response.json();
            console.log('Sections data:', data);

            if (data && data.sections) {
                setSections(data.sections);
                console.log('Sections state updated:', data.sections);
            } else {
                console.error('No sections data in response');
                setSections([]);
            }
        } catch (err) {
            console.error('Error fetching sections:', err);
            setError(err instanceof Error ? err.message : 'Failed to load sections');
            setSections([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchQuestions = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            let url = `${domain}/api/admin/questions`;
            if (selectedSection) {
                url = `${domain}/api/admin/questions/section/${selectedSection}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            const data = await response.json();
            setQuestions(data.questions || []);
        } catch (error) {
            console.error('Error fetching questions:', error);
            setError('Failed to fetch questions');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch(`${domain}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            localStorage.removeItem('adminToken');
            navigate('/admin');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleEditExam = (examId: number) => {
        navigate(`/admin/exams/${examId}`);
    };

    const handleDeleteExam = async (examId: number) => {
        if (!window.confirm('Are you sure you want to delete this exam?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams/${examId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to delete exam');
            }

            setExams(exams.filter(exam => exam.id !== examId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete exam');
        }
    };

    const handleEditSection = (sectionId: number) => {
        navigate(`/admin/sections/${sectionId}`);
    };

    const handleDeleteSection = async (sectionId: number) => {
        if (!window.confirm('Are you sure you want to delete this section?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/sections/${sectionId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to delete section');
            }

            setSections(sections.filter(section => section.id !== sectionId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete section');
        }
    };

    const getStatusColor = (status: Exam['status']) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'draft':
                return 'neutral';
            case 'inactive':
                return 'warning';
            case 'completed':
                return 'primary';
            default:
                return 'neutral';
        }
    };

    const handleEditQuestion = (questionId: number) => {
        navigate(`/admin/questions/${questionId}`);
    };

    const handleDeleteQuestion = async (questionId: number) => {
        if (!window.confirm('Are you sure you want to delete this question?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/questions/${questionId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to delete question');
            }

            setQuestions(questions.filter(question => question.id !== questionId));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete question');
        }
    };

    const handleTabChange = (event: React.SyntheticEvent<Element, Event> | null, newValue: string | number | null) => {
        if (typeof newValue === 'number') {
            setActiveTab(newValue);
            // Update URL without triggering a page reload
            const params = new URLSearchParams(location.search);
            if (newValue === 1) {
                params.set('tab', 'sections');
            } else if (newValue === 2) {
                params.set('tab', 'questions');
            } else {
                params.delete('tab');
            }
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    const handleManageSections = (examId: number) => {
        navigate(`/admin/exams/${examId}/sections`);
    };

    const handleImportSections = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${domain}/api/admin/sections/import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: formData
            });

            if (response.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to import sections');
            }

            // Refresh sections list
            fetchSections();

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to import sections');
        }
    };

    const renderQuestionsTab = (): React.ReactElement => {
        const totals = {
            easy: questions.filter(q => q.difficulty_level === 'easy').length,
            medium: questions.filter(q => q.difficulty_level === 'medium').length,
            hard: questions.filter(q => q.difficulty_level === 'hard').length,
            total: questions.length
        };

        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            onClick={() => navigate('/admin/questions/new')}
                            startDecorator={<AddIcon />}
                        >
                            Create New Question
                        </Button>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <Select
                                    placeholder="Filter by Section"
                                    value={selectedSection?.toString() || ''}
                                    onChange={(_, value) => setSelectedSection(value ? parseInt(value) : null)}
                                >
                                    <Option value="">All Sections</Option>
                                    {sections.map((section) => (
                                        <Option key={section.id} value={section.id.toString()}>
                                            {section.name}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Chip
                                variant="soft"
                                color="primary"
                                size="lg"
                            >
                                Total Questions: {questions.length}
                            </Chip>
                        </Box>
                    </Box>
                </Box>

                <Sheet variant="outlined">
                    <Table>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Section</th>
                                <th>Question</th>
                                <th>Type</th>
                                <th>Difficulty</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((question) => (
                                <tr key={question.id}>
                                    <td>{question.id}</td>
                                    <td>{question.section_name}</td>
                                    <td>
                                        <div dangerouslySetInnerHTML={{
                                            __html: question.question_text.length > 100
                                                ? question.question_text.substring(0, 100) + '...'
                                                : question.question_text
                                        }} />
                                    </td>
                                    <td>{question.question_type}</td>
                                    <td>{question.difficulty_level}</td>
                                    <td>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                                onClick={() => handleEditQuestion(question.id)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={() => handleDeleteQuestion(question.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                    Totals:
                                </td>
                                <td colSpan={2}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            variant="soft"
                                            color="success"
                                            size="sm"
                                        >
                                            Easy: {totals.easy}
                                        </Chip>
                                        <Chip
                                            variant="soft"
                                            color="warning"
                                            size="sm"
                                        >
                                            Medium: {totals.medium}
                                        </Chip>
                                        <Chip
                                            variant="soft"
                                            color="danger"
                                            size="sm"
                                        >
                                            Hard: {totals.hard}
                                        </Chip>
                                        <Chip
                                            variant="soft"
                                            color="primary"
                                            size="sm"
                                        >
                                            Total: {totals.total}
                                        </Chip>
                                    </Box>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Sheet>
            </Box>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1' }}>
            {/* Header */}
            <Sheet
                variant="plain"
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'white',
                    borderBottom: '2px solid',
                    borderColor: '#ea7a23',
                    color: '#ea7a23'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                        alt="Vector India Logo"
                        style={{
                            width: '200px',
                            height: 'auto'
                        }}
                    />
                    <Typography level="h4" sx={{ color: '#ea7a23' }}>Admin Dashboard</Typography>
                </Box>
                <Button
                    variant="outlined"
                    sx={{
                        color: '#ea7a23',
                        borderColor: '#ea7a23',
                        '&:hover': {
                            borderColor: '#ea7a23',
                            backgroundColor: 'rgba(234, 122, 35, 0.1)'
                        }
                    }}
                    onClick={handleLogout}
                    startDecorator={<LogoutIcon />}
                >
                    Logout
                </Button>
            </Sheet>

            {/* Main Content */}
            <Box sx={{ p: 2, maxWidth: '1200px', margin: '0 auto' }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link href="/admin/dashboard">Dashboard</Link>
                    <Typography>
                        {activeTab === 0 ? 'Exam Management' :
                            activeTab === 1 ? 'Section Management' :
                                'Question Management'}
                    </Typography>
                </Breadcrumbs>

                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ mb: 2 }}
                >
                    <TabList>
                        <Tab>Exams</Tab>
                        <Tab>Sections</Tab>
                        <Tab>Questions</Tab>
                    </TabList>
                </Tabs>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {activeTab === 0 && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        onClick={() => navigate('/admin/exams/new')}
                                        startDecorator={<AddIcon />}
                                    >
                                        Create New Exam
                                    </Button>
                                </Box>

                                <Sheet
                                    variant="outlined"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 'sm',
                                        overflow: 'auto',
                                        minHeight: '400px'
                                    }}
                                >
                                    <Table
                                        aria-label="Exams table"
                                        stickyHeader
                                        hoverRow
                                        sx={{
                                            '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                            '--Table-headerUnderlineThickness': '1px',
                                            '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                            'thead th:first-child': { width: '50px' },
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ width: 50, padding: '12px 6px' }}>ID</th>
                                                <th style={{ width: 200 }}>Title</th>
                                                <th style={{ width: 120 }}>Duration</th>
                                                <th style={{ width: 120 }}>Total Marks</th>
                                                <th style={{ width: 120 }}>Passing Marks</th>
                                                <th style={{ width: 120 }}>Status</th>
                                                <th style={{ width: 160 }}>Created At</th>
                                                <th style={{ width: 120 }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {exams.map((exam) => (
                                                <tr key={exam.id}>
                                                    <td style={{ padding: '12px 6px' }}>{exam.id}</td>
                                                    <td>{exam.title}</td>
                                                    <td>{exam.duration} mins</td>
                                                    <td>{exam.total_marks}</td>
                                                    <td>{exam.passing_marks}</td>
                                                    <td>
                                                        <Chip
                                                            variant="soft"
                                                            size="sm"
                                                            color={getStatusColor(exam.status)}
                                                        >
                                                            {exam.status.toUpperCase()}
                                                        </Chip>
                                                    </td>
                                                    <td>{new Date(exam.created_at).toLocaleString()}</td>
                                                    <td>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color="primary"
                                                                onClick={() => handleEditExam(exam.id)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color="danger"
                                                                onClick={() => handleDeleteExam(exam.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color="primary"
                                                                onClick={() => handleManageSections(exam.id)}
                                                            >
                                                                <SettingsIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    {exams.length === 0 && !isLoading && (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography level="body-sm">No exams found</Typography>
                                        </Box>
                                    )}
                                    {isLoading && (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <CircularProgress size="sm" />
                                        </Box>
                                    )}
                                </Sheet>
                            </>
                        )}

                        {activeTab === 1 && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography level="h4">Manage Sections</Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            style={{ display: 'none' }}
                                            ref={fileInputRef}
                                            onChange={handleImportSections}
                                        />
                                        <Button
                                            startDecorator={<UploadFileIcon />}
                                            onClick={() => fileInputRef.current?.click()}
                                            variant="outlined"
                                            color="neutral"
                                        >
                                            Import XLSX
                                        </Button>
                                        <Button
                                            startDecorator={<AddIcon />}
                                            onClick={() => navigate('/admin/sections/new')}
                                        >
                                            Create New Section
                                        </Button>
                                    </Box>
                                </Box>
                                <Sheet variant="outlined">
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Easy Questions</th>
                                                <th>Medium Questions</th>
                                                <th>Hard Questions</th>
                                                <th>Total Questions</th>
                                                <th>Marks/Question</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sections.map((section) => (
                                                <tr key={section.id}>
                                                    <td>{section.name}</td>
                                                    <td>{section.description}</td>
                                                    <td>{section.easy_questions}</td>
                                                    <td>{section.medium_questions}</td>
                                                    <td>{section.hard_questions}</td>
                                                    <td>{section.total_questions}</td>
                                                    <td>{section.marks_per_question}</td>
                                                    <td>{new Date(section.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color="neutral"
                                                                onClick={() => handleEditSection(section.id)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="sm"
                                                                variant="plain"
                                                                color="danger"
                                                                onClick={() => handleDeleteSection(section.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                            </>
                        )}

                        {activeTab === 2 && renderQuestionsTab()}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default AdminDashboard;
